import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Heading } from './common/Catalyst/heading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './common/Catalyst/table';
import { ArrowPathIcon, HandRaisedIcon } from '@heroicons/react/24/outline';
import { StopPaymentReturn } from './clients/rust-api-client';
import { bankService } from './clients/ApiClients';
import { getUserTypeFromToken } from './utils/TokenUtils';
import { canManageStopPayments } from './constants/UserTypes';
import { formatDate, getBankErrorParts } from './utils/bankHelpers';
import {
  canPlaceAgain,
  canRelease,
  describeKey,
  effectiveStatus,
  formatDateTime,
  isOpen,
  statusColor,
  statusLabel,
} from './utils/stopPaymentHelpers';
import { Button } from './common/Catalyst/button';
import { Badge } from './common/Catalyst/badge';
import { FieldPicker } from './common/Custom/FieldPicker';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { Text } from './common/Catalyst/text';
import ShowAlert from './ShowAlert';
import LoadingSpinner from './LoadingSpinner';
import StopPaymentDialog, { StopPaymentMode } from './StopPaymentDialog';
import ReleaseStopPaymentDialog from './ReleaseStopPaymentDialog';

interface HoaStopPaymentsProps {
  hoaId: string;
  /**
   * Bumped by the page when a stop is placed elsewhere (the register's
   * row action), so this list reloads without owning that dialog.
   */
  refreshKey?: number;
  /** Called after a stop is placed or released here, for the register. */
  onChanged?: () => void;
}

interface AlertState {
  type: 'error' | 'success';
  title: string;
  message: string;
}

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: 'open', label: 'Active and in progress' },
  { value: 'active', label: 'Active' },
  { value: 'failed', label: 'Failed' },
  { value: 'released', label: 'Released' },
  { value: 'expired', label: 'Expired' },
  { value: 'all', label: 'All' },
];

/**
 * The stop payments for one association: every stop placed from the
 * register or by number, where each stands with the bank, and the
 * actions on it.
 *
 * Reads are store-only; the page never spends a bank call by being
 * opened. "Refresh from bank" is the one read, and it earns its place
 * only while a stop is active — a quiet list has nothing to ask about.
 */
const HoaStopPayments: React.FC<HoaStopPaymentsProps> = ({
  hoaId,
  refreshKey = 0,
  onChanged,
}) => {
  const userType = useMemo(() => getUserTypeFromToken(), []);
  const canManage = useMemo(() => canManageStopPayments(userType), [userType]);

  const [stops, setStops] = useState<StopPaymentReturn[]>([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<AlertState | null>(null);
  const [filter, setFilter] = useState('open');
  const [dialog, setDialog] = useState<StopPaymentMode | null>(null);
  const [releasing, setReleasing] = useState<StopPaymentReturn | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStops = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await bankService.listHoaStopPayments({ hoaId });
      setStops(resp.data.stop_payments);
    } catch (err) {
      console.error('Error fetching stop payments:', err);
      const { body } = getBankErrorParts(err);
      setAlert({
        type: 'error',
        title: "Couldn't load stop payments",
        message: body || 'Please try again.',
      });
      setStops([]);
    } finally {
      setLoading(false);
    }
  }, [hoaId]);

  useEffect(() => {
    fetchStops();
  }, [fetchStops, refreshKey]);

  const visible = useMemo(() => {
    switch (filter) {
      case 'open':
        return stops.filter(isOpen);
      case 'all':
        return stops;
      default:
        return stops.filter((s) => effectiveStatus(s) === filter);
    }
  }, [stops, filter]);

  // The most recent bank confirmation across the list: what "Active"
  // below is true as of. The bank cannot push a change to us, so a stop
  // released in Online Banking stays Active here until someone refreshes.
  const lastConfirmed = useMemo(() => {
    const seen = stops.map((s) => s.last_seen_at).filter((t): t is string => !!t);
    return seen.length ? seen.sort().slice(-1)[0] : null;
  }, [stops]);

  const handlePlaced = (after: StopPaymentReturn) => {
    fetchStops();
    onChanged?.();
    if (after.status === 'failed') {
      setAlert({
        type: 'error',
        title: `Stop on ${describeKey(after)} recorded, but the bank refused it`,
        message:
          (after.bank_error_code ? `Code ${after.bank_error_code}: ` : '') +
          (after.bank_error_message || 'No detail from the bank.') +
          ' Use “Place again” once the bank is reachable; the check is not protected until it is accepted.',
      });
    } else {
      setAlert({
        type: 'success',
        title: `Stop placed on ${describeKey(after)}`,
        message: after.expiration_date
          ? `The bank will refuse it until ${formatDate(after.expiration_date)}.`
          : 'The bank has accepted the stop.',
      });
    }
  };

  const handleReleased = (after: StopPaymentReturn) => {
    setReleasing(null);
    fetchStops();
    onChanged?.();
    if (after.status === 'released') {
      setAlert({
        type: 'success',
        title: `Stop on ${describeKey(after)} released`,
        message: after.release_note
          ? `${after.release_note.charAt(0).toUpperCase()}${after.release_note.slice(1)}.`
          : 'The check will pay if it is presented from now on.',
      });
    } else {
      setAlert({
        type: 'error',
        title: `The bank refused to release the stop on ${describeKey(after)}`,
        message:
          (after.release_error_message || 'No detail from the bank.') +
          ' The stop is still in force.',
      });
    }
  };

  const handlePlaceAgain = async (stop: StopPaymentReturn) => {
    try {
      setBusyId(stop.id);
      setAlert(null);
      const resp = await bankService.placeHoaStopPaymentAgain({ hoaId, id: stop.id });
      handlePlaced(resp.data);
    } catch (err) {
      const { body } = getBankErrorParts(err);
      setAlert({
        type: 'error',
        title: "Couldn't place the stop",
        message: body || 'Please try again.',
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setAlert(null);
      const resp = await bankService.syncHoaStopPayments({ hoaId });
      setStops(resp.data.stop_payments.stop_payments);
      // Only what concerns stops placed here. The bank's list is a history
      // that keeps every deleted stop, so what it holds beyond our rows —
      // reported as `orphans` and `still_listed` for support — says nothing
      // a manager can act on and is not shown.
      const { active, released_at_bank, expired } = resp.data;
      const notes: string[] = [];
      if (released_at_bank > 0)
        notes.push(
          `${released_at_bank} no longer at the bank, now released here`
        );
      if (expired > 0) notes.push(`${expired} expired`);
      setAlert({
        type: 'success',
        title:
          notes.length > 0
            ? 'Refreshed from the bank'
            : active > 0
              ? `${active} active ${active === 1 ? 'stop' : 'stops'} confirmed at the bank`
              : 'Nothing active to check',
        message:
          notes.length > 0
            ? notes.join('; ') + '.'
            : active > 0
              ? 'Everything matches.'
              : 'No stop placed here is in force; there was nothing for the bank to confirm.',
      });
      onChanged?.();
    } catch (err) {
      const { body } = getBankErrorParts(err);
      setAlert({
        type: 'error',
        title: "Couldn't refresh",
        message: body || 'Please try again.',
      });
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <>
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-start sm:justify-between sm:space-y-0 mb-4">
        <div>
          <Heading level={4}>Stop Payments</Heading>
          <Text className="mt-1 text-sm">
            Checks the bank has been told to refuse. Stop a check from the
            register, or by number here; release it when the check is found
            or reissued.
          </Text>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <Button plain onClick={handleRefresh} disabled={refreshing}>
              <ArrowPathIcon
                className={`h-5 w-5 mr-1 ${refreshing ? 'animate-spin' : ''}`}
              />
              {refreshing ? 'Asking the bank…' : 'Refresh from bank'}
            </Button>
            <Button plain onClick={() => setDialog({ kind: 'range' })}>
              Stop a range
            </Button>
            <Button color="sky" onClick={() => setDialog({ kind: 'check' })}>
              <HandRaisedIcon className="h-5 w-5 mr-2" />
              Stop a check
            </Button>
          </div>
        )}
      </div>

      {alert && alert.type === 'error' && (
        <ErrorAlert
          className="mb-4"
          title={alert.title}
          onDismiss={() => setAlert(null)}
        >
          {alert.message}
        </ErrorAlert>
      )}
      {alert && alert.type === 'success' && (
        <ShowAlert
          alertType="success"
          alertTitle={alert.title}
          alertMessage={alert.message}
          isVisible={!!alert}
          onClose={() => setAlert(null)}
          timeout={12000}
          isInline={true}
        />
      )}

      <div className="bg-stone-100 dark:bg-gray-600 rounded-lg shadow transition-colors duration-200 overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <Text className="text-sm">Show</Text>
            <FieldPicker
              size="sm"
              className="w-56"
              label="Show"
              value={filter}
              onChange={setFilter}
              options={STATUS_FILTERS}
            />
            <Text className="ml-auto text-xs">
              {lastConfirmed
                ? `Last confirmed with the bank ${formatDateTime(lastConfirmed)}. The bank cannot tell us about changes made elsewhere; refresh to check.`
                : 'This list is our record. Refresh to compare it with the bank.'}
            </Text>
          </div>
          {loading ? (
            <LoadingSpinner />
          ) : visible.length === 0 ? (
            <Text>
              {stops.length === 0
                ? 'No stop payments.'
                : 'No stop payments match this filter.'}
              {stops.length === 0 && canManage
                ? ' Stop a check from the register, or by number here.'
                : ''}
            </Text>
          ) : (
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <Table>
                    <TableHead className="bg-stone-100 dark:bg-gray-600">
                      <TableRow>
                        <TableHeader className="text-base font-semibold">
                          Account
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Stopped
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Reason
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Status
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Placed
                        </TableHeader>
                        {canManage && (
                          <TableHeader className="text-base font-semibold">
                            <span className="sr-only">Actions</span>
                          </TableHeader>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visible.map((s) => {
                        const status = effectiveStatus(s);
                        return (
                          <TableRow key={s.id}>
                            <TableCell className="tabular-nums">
                              {s.account_number}
                            </TableCell>
                            <TableCell className="font-medium tabular-nums">
                              {describeKey(s)}
                              {s.kind === 'range' && (
                                <Text className="text-xs">Range</Text>
                              )}
                              {s.hoa_check_id && (
                                <Text className="text-xs">From the register</Text>
                              )}
                            </TableCell>
                            <TableCell>{s.reason}</TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <div>
                                  <Badge color={statusColor(status)}>
                                    {statusLabel(s)}
                                  </Badge>
                                </div>
                                {status === 'failed' && (
                                  <Text className="text-xs !text-danger">
                                    {s.bank_error_code
                                      ? `Code ${s.bank_error_code}: `
                                      : ''}
                                    {s.bank_error_message || 'The bank refused the stop'}
                                  </Text>
                                )}
                                {status === 'active' && (
                                  <Text className="text-xs">
                                    {s.last_seen_at
                                      ? `Confirmed by the bank ${formatDateTime(s.last_seen_at)}`
                                      : 'Not yet confirmed by the bank'}
                                  </Text>
                                )}
                                {status === 'active' && s.release_error_message && (
                                  <Text className="text-xs !text-danger">
                                    Release refused: {s.release_error_message}
                                  </Text>
                                )}
                                {status === 'released' && (
                                  <Text className="text-xs">
                                    {s.released_by
                                      ? `${formatDate(s.released_at)}`
                                      : s.release_note || 'Released'}
                                  </Text>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Text className="text-xs">
                                {formatDate(s.placed_at ?? s.date_created)}
                              </Text>
                            </TableCell>
                            {canManage && (
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {canPlaceAgain(s) && (
                                    <Button
                                      plain
                                      disabled={busyId === s.id}
                                      onClick={() => handlePlaceAgain(s)}
                                    >
                                      {busyId === s.id ? 'Placing…' : 'Place again'}
                                    </Button>
                                  )}
                                  {canRelease(s) && (
                                    <Button
                                      plain
                                      disabled={busyId === s.id}
                                      onClick={() => setReleasing(s)}
                                    >
                                      {status === 'active' ? 'Release' : 'Remove'}
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <StopPaymentDialog
        mode={dialog}
        hoaId={hoaId}
        onClose={() => setDialog(null)}
        onPlaced={handlePlaced}
      />
      <ReleaseStopPaymentDialog
        stop={releasing}
        hoaId={hoaId}
        onClose={() => setReleasing(null)}
        onReleased={handleReleased}
      />
    </>
  );
};

export default HoaStopPayments;
